/**
 * The agents and the scopes they work in. Mirrors
 * `caseblink/case_agent/registry.py` and `case_agent/scope.py`.
 *
 * One `assistant` works a case, a client or the whole practice (its *focus*
 * is the thread's scope). The pre-merge kinds (`case_assistant`,
 * `client_manager`, `practice_manager`) still appear on stored threads,
 * messages and activity: `normalizeAgentKind` maps them to `assistant`.
 */

export type AgentKind =
  | "assistant"
  /** Internal: writes the firm's skills; lives on the practice scope. */
  | "skill_builder"
  /** Internal: the guided first run (`/onboarding`); lives on the practice scope. */
  | "onboarding";

/** Kinds retired by the merge, kept readable on old data. */
export type LegacyAgentKind =
  | "case_assistant"
  | "client_manager"
  | "practice_manager";

export type AnyAgentKind = AgentKind | LegacyAgentKind;

const LEGACY_AGENT_KINDS: Record<LegacyAgentKind, AgentKind> = {
  case_assistant: "assistant",
  client_manager: "assistant",
  practice_manager: "assistant"
};

export const isAgentKind = (v: unknown): v is AgentKind =>
  v === "assistant" || v === "skill_builder" || v === "onboarding";

/** The current kind for any stored kind string; `fallback` for unknown ones. */
export const normalizeAgentKind = (
  kind: unknown,
  fallback: AgentKind = "assistant"
): AgentKind => {
  if (isAgentKind(kind)) return kind;
  if (typeof kind === "string" && kind in LEGACY_AGENT_KINDS)
    return LEGACY_AGENT_KINDS[kind as LegacyAgentKind];
  return fallback;
};

export type ScopeKind = "case" | "client" | "practice";

export type AgentScope = {
  kind: ScopeKind;
  /** individual_id (case), foreign_national_id (client) or practice name. */
  id: string;
  /** Human label when known (case title, client name, firm). */
  displayName?: string | null;
};

/** `GET /api/agents` row. */
export type AgentSpec = {
  kind: AgentKind;
  /** The default scope; `scope_kinds` lists every scope the agent serves. */
  scope_kind: ScopeKind;
  scope_kinds?: ScopeKind[];
  label: string;
  description: string;
  greeting: string;
  starters: string[];
  /** Greeting, starters and built-in skills per focus, for agents that serve several scopes. */
  by_focus?: Partial<Record<ScopeKind, AgentSpecAtFocus>>;
  supports_plan_mode: boolean;
  /** Pre-merge kinds this spec answers for. */
  aliases?: string[];
};

/**
 * A skill the harness ships (`case_agent/builtin_skills.py`), as the
 * composer's picker lists it next to the firm's own. Its id is `builtin:<slug>`.
 */
export type BuiltinSkillSummary = {
  id: string;
  slug: string;
  title: string;
  description: string;
  focuses: ScopeKind[];
  builtin: true;
};

export type AgentSpecAtFocus = {
  greeting: string;
  starters: string[];
  skills?: BuiltinSkillSummary[];
};

/** The greeting, starters and built-in skills a spec shows at one focus. */
export const specAtFocus = (
  spec: AgentSpec,
  focus: ScopeKind
): AgentSpecAtFocus =>
  spec.by_focus?.[focus] ?? {
    greeting: spec.greeting,
    starters: spec.starters
  };

/** One starter from `GET /api/agents/<kind>/<id>/starters`. */
export type AgentStarter = {
  text: string;
  /** Why it is offered: `static`, or what in the matter prompted it (`expiration`, `deadline`, `intake`, `reminder`, `rfe`, `status`). */
  reason: string;
};

export type ModelProvider = "anthropic" | "openai" | "google_genai";

/** One entry of the model picker (`case_agent/model.py` `MODEL_CATALOG`). */
export type AgentModel = {
  id: string;
  label: string;
  provider: ModelProvider;
  supports_thinking: boolean;
  tier?: "efficient" | "standard" | "frontier";
  description: string;
  /** Reads PDF pages and images natively (`read_document` sends the slice). */
  reads_documents?: boolean;
};

/**
 * A stop of the picker's speed / depth slider under Auto: Quick (`cost`),
 * Balanced (`balanced`) or Thorough (`intelligence`). The ids are the router's
 * and are stored on threads; only the labels are shown.
 */
export type OptimizeFor = "cost" | "balanced" | "intelligence";

export type AutoMode = {
  id: OptimizeFor;
  label: string;
  /** The catalog id every turn on this stop runs on. */
  model_id?: string;
  /** Relative effort, never a duration. */
  description: string;
  /** Immigration tasks this stop suits. */
  examples?: string[];
};

export type AgentsCatalog = {
  agents: AgentSpec[];
  /** The caller's practice scope, or `null` when they have no practice. */
  practice: {
    scope_kind: "practice";
    scope_id: string;
    display_name: string;
  } | null;
  /** Models whose provider is configured, in catalog order. */
  models: AgentModel[];
  auto_modes: AutoMode[];
};

/** The `Auto` entry of the picker; the server routes per turn. */
export const AUTO_MODEL_ID = "auto";

/**
 * The server's default model (`DEFAULT_MODEL` in `case_agent/model.py`) and the
 * model the Managed Deep Agents run on: what Settings > Agents calls "Default".
 * The composer starts on Auto, not on this.
 */
export const DEFAULT_MODEL_ID = "gpt-6-luna";

/**
 * The "Inherit from parent" entry of the Subagent model setting (Settings >
 * Agents): the Managed Deep Agents run on the case agent turn's model when
 * started from a turn, and on the default when started from the UI. Stored on
 * the lawyer as `subagentModel`; `""` there means Default.
 */
export const SUBAGENT_INHERIT_ID = "inherit";

export const PROVIDER_LABEL: Record<ModelProvider, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  google_genai: "Google"
};

/** `data-model` part / `metadata.model` on an assistant message. */
export type ModelUsed = {
  id: string;
  label: string;
  routed: boolean;
  thinking: boolean;
  /** The slider stop a routed turn ran under. */
  optimize_for?: OptimizeFor;
  /** A sibling answered after the primary failed. */
  fallback?: boolean;
  /** Why the primary failed: `rate_limit`, `timeout`, `outage`, `context_length`, `error`. */
  fallback_reason?: string;
  /** The model the turn was meant for, when a fallback answered. */
  primary_model?: { id: string; label: string };
  /** "Answered by Gemini 3.8 Flash because GPT-6.1 Sol timed out". */
  explanation?: string;
  /** The fallback was a less capable tier than the primary. */
  downgraded?: boolean;
  /** …and the turn wrote a draft. */
  drafted?: boolean;
  /** How warm the prompt cache was under this decision (in history only). */
  cache?: {
    calls: number;
    input_tokens: number;
    cache_read: number;
    hit_rate: number;
  };
};

export const AGENT_FOR_SCOPE: Record<ScopeKind, AgentKind> = {
  case: "assistant",
  client: "assistant",
  practice: "assistant"
};

/** The scopes each agent serves (the first is its default). */
export const SCOPES_FOR_AGENT: Record<AgentKind, ScopeKind[]> = {
  assistant: ["case", "client", "practice"],
  skill_builder: ["practice"],
  onboarding: ["practice"]
};

/** The assistant's one name, wherever it is talked to. */
export const ASSISTANT_PERSONA = "Casey";

export const AGENT_LABEL: Record<AgentKind, string> = {
  assistant: ASSISTANT_PERSONA,
  skill_builder: "Automation Builder",
  onboarding: "Onboarding"
};

/** The label for any stored kind string (legacy kinds read as the assistant). */
export const agentLabel = (kind: unknown): string =>
  AGENT_LABEL[normalizeAgentKind(kind)];

/**
 * The assistant's titles before it was Casey, longest first. Stored rows
 * (activity, notes, notifications, mentions, handoffs) still carry them.
 */
const LEGACY_ASSISTANT_TITLES = [
  "Practice Assistant",
  "Practice Manager",
  "Client Assistant",
  "Client Manager",
  "Case Assistant",
  "CaseBlink Agent",
  "CaseBlink AI",
  "AI Assistant",
  "Case Agent",
  "Assistant",
  "Agent"
] as const;

const LEGACY_TITLE_RE = new RegExp(
  `^(?:the )?(?:${LEGACY_ASSISTANT_TITLES.join("|")})(?=$|[\\s·:—-])`
);

/**
 * A stored agent label as Casey: a leading legacy title becomes "Casey" and
 * the rest is kept ("Case Assistant · O-1A" -> "Casey · O-1A", "Case
 * Assistant needs your approval in X" -> "Casey needs your approval in X").
 * Anything else, including lowercase "the assistant" prose, is left alone.
 */
export const caseyName = (label: string): string =>
  label.replace(LEGACY_TITLE_RE, ASSISTANT_PERSONA);

/**
 * How an agent introduces itself: the assistant is Casey at every focus (the
 * focus is a subtitle, `focusSubtitle`); other agents keep their label.
 */
export const agentLabelAt = (
  kind: unknown,
  _focus?: ScopeKind | null
): string => {
  const normalized = normalizeAgentKind(kind);
  return normalized === "assistant"
    ? ASSISTANT_PERSONA
    : AGENT_LABEL[normalized];
};

/** What a thread's home reads as under Casey's name ("Case", "Client", "Practice"). */
export const focusSubtitle = (
  focus: ScopeKind | null | undefined
): string | null =>
  focus === "case"
    ? "Case"
    : focus === "client"
    ? "Client"
    : focus === "practice"
    ? "Practice"
    : null;

/** Where a thread came from when another agent handed work over (pre-merge). */
export type ThreadHandoff = {
  from_thread_id?: string | null;
  from_scope_kind?: ScopeKind;
  from_scope_id?: string;
  from_agent_kind?: AnyAgentKind;
  by_uid?: string;
};

/**
 * `handoff_to_agent` tool output (also the `data-handoff` part, minus
 * `reply`). The tool is retired; old transcripts still carry these.
 */
export type HandoffResult = {
  agent: AnyAgentKind;
  agent_label: string;
  scope_kind: ScopeKind;
  scope_id: string;
  scope_name?: string | null;
  thread_id: string;
  thread_title?: string | null;
  title?: string | null;
  link: string;
  reply?: string;
  awaiting_approval?: boolean;
  error?: string;
};

/** `ask_agent` tool output (retired tool; old transcripts). */
export type AskAgentResult = {
  agent: AnyAgentKind;
  agent_label: string;
  scope_kind: ScopeKind;
  scope_id: string;
  scope_name?: string | null;
  answer: string;
  error?: string;
};

/** `delegate_to_assistant` tool output: heavy work on one case or client, run inside the turn. */
export type DelegationResult = {
  /** `running`: still working in the background; this thread is told when it finishes. */
  status: "completed" | "running";
  scope_kind: ScopeKind;
  scope_id: string;
  /** Markdown link to the case or client. */
  scope_ref?: string | null;
  scope_name?: string | null;
  reply?: string | null;
  tools_called?: string[];
  change_ids?: string[];
  /** Per write, the ids it created or changed (`{tool, draft_id, ...}`), for the agent's next calls. */
  created?: Record<string, string>[];
  tool_errors?: number;
  seconds?: number;
  note?: string | null;
  error?: string;
  /** The thread that delegated; Continue in Case reads the run back from it. */
  thread_id?: string | null;
};

/** The approval policy's verdict on a pending call (`data-approval-review`). */
export type ApprovalReview = {
  approvalId: string;
  toolCallId: string;
  tool: string;
  decision: "allow" | "ask" | "deny";
  reason: string;
  /**
   * The policy rule that decided (`off_focus`, `cross_client`, `tainted`,
   * `fan_out`, `outbound`, ...). An `off_focus` grant covers this target only.
   */
  rule?: string;
  description?: string;
  /** The case or client the call changes, when it is not the turn's focus. */
  target?: { kind: ScopeKind; id: string; label?: string | null } | null;
  /** The thread's home kind (`case`, `client`, `practice`, ...). */
  home_kind?: string | null;
  /** Every client a bulk call reaches (`create_client_reminders`). */
  targets?: Array<{ kind: ScopeKind; id: string; label?: string | null }>;
  /** Who an outbound message reaches (client email, intake, connected app). */
  recipient?: string | null;
  reversible: boolean;
  outbound: boolean;
  /** Already allowed for this thread by the acting person. */
  granted: boolean;
  /** "Allow for this thread" may be offered. */
  grantable: boolean;
  allowed_decisions: Array<"approve" | "edit" | "reject">;
  /** Content from outside the firm read this turn (documents, intake answers, email). */
  tainted_sources: string[];
  /** Show the full body before sending: the turn read untrusted content. */
  show_body: boolean;
  /** What the card can preview of what goes out (the intake an email links to). */
  preview?: ApprovalPreview | null;
  allow_for_thread: boolean;
};

export type ApprovalPreview =
  | {
      kind: "intake";
      id: string;
      foreign_national_id: string | null;
    }
  | FirmSettingsPreview;

/** A `save_firm_settings` card: the section and what it sets for everyone. */
export type FirmSettingsPreview = {
  kind: "firm_settings";
  section: string;
  /** Lower case ("firm details"); the card title-cases it. */
  label: string;
  /** Firm instructions, shown in full. */
  text?: string;
  /** Any other section's patch. */
  fields?: Record<string, unknown>;
};

/** One answer to an approval request (`addToolApprovalResponse`). */
export type ApprovalDecision = {
  id: string;
  approved: boolean;
  /** A rejection's instruction to the agent, or a note on an approval. */
  reason?: string;
  /** Approve with changed arguments. */
  edited_args?: Record<string, unknown>;
  /** Do not ask again for this tool on this thread (this person's turns). */
  allow_for_thread?: boolean;
};

/** A recorded, undoable change (`data-change`; `GET .../changes`). */
export type AgentChange = {
  id: string;
  thread_id: string;
  turn_id?: string | null;
  tool_call_id?: string | null;
  tool: string;
  kind: string;
  label: string;
  /**
   * `time` is the acting person's own timesheet, not a scope; the settings
   * kinds are the member's or the firm's settings (Settings modal).
   */
  target_kind?:
    | ScopeKind
    | "time"
    | "lawyer_settings"
    | "firm_settings"
    | "agent_switches"
    | "intake_template"
    | null;
  target_id?: string | null;
  /** Whose record changed (the client's name), for a case or client target. */
  target_label?: string | null;
  focus_kind?: ScopeKind | null;
  focus_id?: string | null;
  delegated_from?: string | null;
  actor_uid?: string | null;
  status: "applied" | "undone" | "superseded" | string;
  created_at?: string | null;
  undone_at?: string | null;
  undone_by?: string | null;
  note?: string | null;
  undoable: boolean;
  /** Exhibit labels this change moved (tree changes); `after` null = label lost. */
  relabeled?: Array<{
    node_id: string;
    name?: string | null;
    before: string | null;
    after: string | null;
  }>;
  /** What the write updated on its own (citations re-pointed, an exhibit
   * list brought in line), one line each; Undo reverses them too. */
  follow_on?: string[];
  /** What undoing it did to those updates, when anything was left as is. */
  follow_on_undone?: string | null;
  /** Files this tree change retitled; the rows load with `changeRenames`. */
  renamed_total?: number;
};

/**
 * `data-follow-ups`: the reply left case-integrity items for later (a draft
 * citing a removed page, a stale packet). The list loads from the case.
 */
export type FollowUpsData = {
  individual_id: string;
  thread_id?: string | null;
  count: number;
};

/** One file a tree change retitled (`GET .../changes/:id/renames`). */
export type ChangeRenameRow = {
  node_id: string;
  before: string;
  after: string;
  folder: string;
  /** Its title was put back since. */
  reverted: boolean;
};

export type UndoTitlesResult = {
  change: AgentChange;
  reverted: string[];
  refused: Array<{ node_id: string; reason: string }>;
};

export type UndoTurnResult = {
  undone: AgentChange[];
  refused: Array<{ change: AgentChange; reason: string }>;
};

export const sameScope = (
  a: AgentScope | null,
  b: AgentScope | null
): boolean => !!a && !!b && a.kind === b.kind && a.id === b.id;

/** `"<kind>:<id>"`, the key the realtime hub and notifications use. */
export const scopeKey = (scope: AgentScope): string =>
  `${scope.kind}:${scope.id}`;
